import { createContext, useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Pin, PinOff, Search } from "lucide-react";
import { useI18n } from "../../i18n";

export const NavCtx = createContext(null);
const norm = (s) => s.toLocaleLowerCase("el").normalize("NFD").replace(/[\u0300-\u036f]/g, "");

// One line per destination: icon · name · badge · pin · chevron. Groups are large headings so the eye scans down a list, not a wall of tiles.
const Row = ({ m }) => {
  const { t } = useI18n();
  const { badgeOf, prefs, setPrefs, onGo, onAction } = useContext(NavCtx);
  const Icon = m.icon;
  const n = badgeOf(m);
  const pinned = prefs.pinned.includes(m.key);
  const pin = (e) => { e.preventDefault(); e.stopPropagation(); setPrefs({ pinned: pinned ? prefs.pinned.filter((x) => x !== m.key) : [...prefs.pinned, m.key] }); };
  const body = (
    <>
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy/5 text-navy transition-colors group-hover:bg-brand/25"><Icon className="h-5 w-5" /></span>
      <span className="min-w-0 flex-1 truncate text-base font-medium text-navy">{t(`snav.item.${m.key}`)}</span>
      {n > 0 && <span className="grid h-6 min-w-6 place-items-center rounded-full bg-brand px-1.5 text-xs font-bold text-navy" data-testid={`tile-badge-${m.key}`}>{n > 99 ? "99+" : n}</span>}
      {m.to && <span role="button" tabIndex={0} onClick={pin} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") pin(e); }} aria-label={t(pinned ? "snav.unpin" : "snav.pin")} data-testid={`tile-pin-${m.key}`}
        className={`grid h-9 w-9 place-items-center rounded-full text-slate-400 hover:text-navy ${pinned ? "" : "lg:opacity-0 lg:group-hover:opacity-100"}`}>{pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}</span>}
      <ChevronRight className="h-4 w-4 shrink-0 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-navy" />
    </>
  );
  const cls = "group flex min-h-[56px] w-full items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-canvas";
  return m.to
    ? <Link to={m.to} onClick={() => onGo(m)} className={cls} data-testid={`tile-${m.key}`}>{body}</Link>
    : <button type="button" onClick={() => onAction(m.action)} className={cls} data-testid={`tile-${m.key}`}>{body}</button>;
};

const List = ({ items }) => <ul className="divide-y divide-slate-100 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-100">{items.map((m) => <li key={m.key}><Row m={m} /></li>)}</ul>;

const Section = ({ title, sub, items, testId }) => (
  <section className="min-w-0 space-y-3" data-testid={testId}>
    {title && <div><h2 className="text-xl font-bold text-navy md:text-2xl">{title}</h2>{sub && <p className="text-sm text-slate-500">{sub}</p>}</div>}
    <List items={items} />
  </section>
);

export default function StaffMenu() {
  const { t } = useI18n();
  const { groups, prefs } = useContext(NavCtx);
  const [q, setQ] = useState("");
  const all = useMemo(() => groups.flatMap((g) => g.items.map((m) => ({ ...m, group: g.key }))), [groups]);
  const needle = norm(q.trim());
  const hits = needle ? all.filter((m) => norm(`${t(`snav.item.${m.key}`)} ${t(`snav.group.${m.group}`)}`).includes(needle)) : [];
  const pinned = all.filter((m) => prefs.pinned.includes(m.key));
  const sub = (g) => { const s = t(`snav.groupSub.${g.key}`); return s === `snav.groupSub.${g.key}` ? null : s; };
  return (
    <div className="space-y-8" data-testid="staff-menu-page">
      <label className="relative block max-w-xl">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
        <input autoFocus={false} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("snav.search")} data-testid="staff-menu-search"
          className="h-14 w-full rounded-2xl border border-slate-200 bg-white pl-12 pr-4 text-base shadow-sm focus:border-navy focus:outline-none" />
      </label>
      {needle ? (
        <section data-testid="staff-menu-results">{hits.length ? <List items={hits} /> : <p className="text-sm text-slate-500">{t("snav.noHits")}</p>}</section>
      ) : (
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 xl:grid-cols-3">
          {pinned.length > 0 && <Section title={t("snav.pinned")} items={pinned} testId="staff-menu-pinned" />}
          {groups.map((g) => <Section key={g.key} title={g.standalone ? null : t(`snav.group.${g.key}`)} sub={g.standalone ? null : sub(g)} items={g.items} testId={`staff-group-${g.key}`} />)}
        </div>
      )}
    </div>
  );
}
